"use client";

import { Button, buttonClassNames, EmptyState } from "@hisani/ui";
import { useInfiniteQuery } from "@tanstack/react-query";
import { ArrowLeft, ChevronRight, MessageCircle } from "lucide-react";
import { useLocale, useTranslations } from "next-intl";
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "../../../i18n/navigation";
import { loginPath } from "../../../i18n/paths";
import { useAuth } from "../../auth-client";
import { ConversationDialog } from "./conversation-dialog";
import {
  marketplaceConversationQueryOptions,
  marketplaceConversationFromPages,
  marketplaceConversationsQueryOptions,
} from "./marketplace-queries";

export function MarketplaceConversationsPage() {
  const t = useTranslations("Marketplace");
  const locale = useLocale();
  const auth = useAuth();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const inboxQuery = useInfiniteQuery(
    marketplaceConversationsQueryOptions({
      enabled: auth.status === "authenticated",
    }),
  );
  const conversationQuery = useInfiniteQuery(
    marketplaceConversationQueryOptions(selectedId),
  );
  const selectedConversation = useMemo(
    () => marketplaceConversationFromPages(conversationQuery.data),
    [conversationQuery.data],
  );
  const conversations = useMemo(
    () => inboxQuery.data?.pages.flatMap((page) => page.conversations) ?? [],
    [inboxQuery.data],
  );
  const selectedSummary = conversations.find(
    (conversation) => conversation.id === selectedId,
  );
  const fetchNextConversationPage = inboxQuery.fetchNextPage;

  useEffect(() => {
    if (auth.status === "anonymous") {
      window.location.replace(loginPath(locale, "/marketplace/conversations"));
    }
  }, [auth.status, locale]);

  return (
    <div className="bg-canvas">
      <div className="mx-auto grid max-w-3xl gap-2 px-[var(--space-page-inline)] pt-0 pb-2 sm:px-6 sm:py-5 lg:px-8">
        <Link
          href="/marketplace"
          className="inline-flex min-h-11 w-fit items-center gap-2 rounded-md pe-3 text-body-small font-semibold text-secondary hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus"
        >
          <ArrowLeft className="size-5 rtl:rotate-180" aria-hidden="true" />
          {t("backToMarketplace")}
        </Link>

        <header className="grid gap-1">
          <h1 className="text-heading-large text-primary">
            {t("conversationsTitle")}
          </h1>
          <p className="text-body text-secondary">
            {t("conversationsDescription")}
          </p>
        </header>

        {auth.status !== "authenticated" || inboxQuery.isPending ? (
          <div
            role="status"
            aria-label={t("conversationsLoading")}
            className="divide-y divide-border-subtle border-y border-border-subtle"
          >
            <div className="h-24 w-full animate-pulse bg-subtle" />
            <div className="h-24 w-full animate-pulse bg-subtle" />
            <span className="sr-only">{t("conversationsLoading")}</span>
          </div>
        ) : inboxQuery.isError && conversations.length === 0 ? (
          <div className="border-y border-border-subtle py-4 text-body-small">
            <p role="alert" className="font-semibold text-danger">
              {t("conversationsError")}
            </p>
            <Button
              type="button"
              variant="secondary"
              className="mt-4 min-h-12"
              onClick={() => void inboxQuery.refetch()}
            >
              {t("retry")}
            </Button>
          </div>
        ) : conversations.length === 0 ? (
          <EmptyState
            icon={MessageCircle}
            title={t("noConversationsTitle")}
            description={t("noConversations")}
            action={
              <Link
                href="/marketplace"
                className={buttonClassNames({
                  variant: "accent",
                  size: "lg",
                })}
              >
                {t("browseListings")}
              </Link>
            }
          />
        ) : (
          <div className="grid gap-4">
            <ul className="divide-y divide-border-subtle border-y border-border-subtle bg-surface">
              {conversations.map((conversation) => {
                const isSeller = conversation.sellerId === auth.account.id;
                const activity =
                  conversation.lastMessage?.createdAt ?? conversation.updatedAt;
                return (
                  <li key={conversation.id}>
                    <Button
                      type="button"
                      variant="ghost"
                      aria-label={t("openConversationName", {
                        name: conversation.listingHorseName,
                      })}
                      className="min-h-[5.5rem] w-full justify-between gap-3 rounded-none px-1 py-2 text-start sm:px-3"
                      onClick={() => setSelectedId(conversation.id)}
                    >
                      <div className="min-w-0 flex-1">
                        <h2
                          dir="auto"
                          className="text-base leading-5 font-bold text-primary"
                        >
                          {conversation.listingHorseName}
                        </h2>
                        <p className="mt-0.5 text-xs leading-4 font-normal text-secondary">
                          {isSeller
                            ? t("buyerConversation")
                            : t("sellerConversation")}
                        </p>
                        <p
                          dir="auto"
                          className="mt-0.5 line-clamp-1 text-sm leading-5 font-normal text-primary"
                        >
                          {conversation.lastMessage?.body ??
                            t("conversationStarted")}
                        </p>
                        <time
                          dateTime={activity}
                          className="mt-0.5 block text-xs leading-4 font-normal text-tertiary"
                        >
                          {new Intl.DateTimeFormat(locale, {
                            month: "short",
                            day: "numeric",
                            hour: "numeric",
                            minute: "2-digit",
                          }).format(new Date(activity))}
                        </time>
                      </div>
                      <ChevronRight
                        className="size-5 shrink-0 text-tertiary rtl:rotate-180"
                        aria-hidden="true"
                      />
                    </Button>
                  </li>
                );
              })}
            </ul>
            {inboxQuery.isFetchNextPageError ? (
              <div
                role="alert"
                className="flex flex-wrap items-center justify-center gap-3 text-body-small font-semibold text-danger"
              >
                <span>{t("conversationsLoadMoreError")}</span>
                <Button
                  type="button"
                  variant="secondary"
                  className="min-h-12"
                  onClick={() => void fetchNextConversationPage()}
                >
                  {t("retry")}
                </Button>
              </div>
            ) : inboxQuery.hasNextPage ? (
              <Button
                type="button"
                variant="secondary"
                className="min-h-12 justify-self-center"
                disabled={inboxQuery.isFetchingNextPage}
                onClick={() => void fetchNextConversationPage()}
              >
                {inboxQuery.isFetchingNextPage
                  ? t("loadingMoreConversations")
                  : t("loadMoreConversations")}
              </Button>
            ) : null}
          </div>
        )}
      </div>
      <ConversationDialog
        open={selectedId !== null}
        title={selectedSummary?.listingHorseName ?? null}
        participantContext={
          selectedSummary === undefined || auth.status !== "authenticated"
            ? null
            : selectedSummary.sellerId === auth.account.id
              ? t("buyerConversation")
              : t("sellerConversation")
        }
        conversation={selectedConversation}
        accountId={
          auth.status === "authenticated" ? auth.account.id : undefined
        }
        loading={selectedId !== null && conversationQuery.isPending}
        conversationError={
          selectedId !== null &&
          conversationQuery.isError &&
          selectedConversation === null
        }
        hasOlderMessages={conversationQuery.hasNextPage}
        isLoadingOlderMessages={conversationQuery.isFetchingNextPage}
        olderMessagesFailed={conversationQuery.isFetchNextPageError}
        onLoadOlderMessages={() => void conversationQuery.fetchNextPage()}
        onRetry={() => void conversationQuery.refetch()}
        onClose={() => setSelectedId(null)}
      />
    </div>
  );
}
